import { SearchModes } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { FLASH_SALES_ICON } from '@/app/common/icons/promotionIcons';
import { type ControlProps, ControlType, FieldType } from '../../../../Form';
import FilterContainer from '../../FilterContainer';
import type { CommonProps } from '../CommonProps';
import DiscountToggleLabel from './DiscountToggleLabel';
import { getIsPinkPeopleEnabled } from '../../../../../pinkpeople/utils';
import type { Localization } from '../../../../../common/Localization';

const isApplied = (searchParams: SearchParams, param: URLParameters): boolean => searchParams[param] === '1';

const getDiscountFields = ({
  searchParams,
  searchInfoData,
  imageBasePath,
  searchMode,
  isWaiting,
  commonProps,
  commonData,
  localization,
  t,
}: {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  imageBasePath: string;
  searchMode: SearchModes;
  isWaiting: boolean;
  commonProps: CommonProps;
  commonData: CommonData;
  localization: Localization;
  t: TranslationFunction;
}): ControlProps => {
  const { dynamicFiltersResponse, isMobile, isSidebar } = commonProps;
  const loggedIn = commonData.authResponse?.loggedIn;
  const isHO = searchMode === SearchModes.HO;
  const showFlashSales =
    isApplied(searchParams, URLParameters.FLASH_SALES) || dynamicFiltersResponse?.promotions?.flash_sales !== undefined;
  const showLoyaltyDeals =
    isApplied(searchParams, URLParameters.FILTER_LOYALTY_DEALS) ||
    (isHO && dynamicFiltersResponse?.promotions?.loyalty_deals !== undefined);
  const showTSH =
    isApplied(searchParams, URLParameters.TOP_SECRET_HOTEL) ||
    (isHO && dynamicFiltersResponse?.promotions?.tsh !== undefined);
  const showMyPrice =
    isApplied(searchParams, URLParameters.MY_PRICE) ||
    (loggedIn && isHO && dynamicFiltersResponse?.promotions?.myprice !== undefined);

  const myPriceLabel = t('vacation.oss.cards.myprice_label');
  const showPinkPeople = getIsPinkPeopleEnabled({
    searchInfoData,
    commonData,
    localization,
  });
  const lastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;

  const controls = [
    ...(showPinkPeople
      ? [
          {
            control: ControlType.CHECKBOX,
            name: URLParameters.PINK_PEOPLE,
            label: {
              text: (
                <DiscountToggleLabel
                  label="Pink People"
                  icon="pinkpeople"
                  imageBasePath={imageBasePath}
                  countOnTheLeft={!isMobile && !isSidebar}
                  isDesktop={!isMobile}
                  tooltipText="Pink People Deals"
                  disabled={isWaiting}
                  isFirst={true}
                />
              ),
            },
            value: searchParams[URLParameters.PINK_PEOPLE] === '1',
            type: FieldType.TOGGLE,
            disabled: isWaiting,
          },
        ]
      : []),
    ...(showLoyaltyDeals
      ? [
          {
            control: ControlType.CHECKBOX,
            name: URLParameters.FILTER_LOYALTY_DEALS,
            label: {
              text: (
                <DiscountToggleLabel
                  label={t('vacation.oss.filters.loyalty.label')}
                  icon="color/loyalty_black"
                  imageBasePath={imageBasePath}
                  countOnTheLeft={!isMobile && !isSidebar}
                  count={dynamicFiltersResponse?.promotions?.loyalty_deals}
                  isDesktop={!isMobile}
                  tooltipText={t('vacation.oss.filters.loyalty.tooltip')}
                  disabled={dynamicFiltersResponse?.promotions?.loyalty_deals === 0}
                  isFirst={!showPinkPeople}
                />
              ),
            },
            value: searchParams[URLParameters.FILTER_LOYALTY_DEALS] === '1',
            type: FieldType.TOGGLE,
            disabled: isWaiting || dynamicFiltersResponse?.promotions?.loyalty_deals === 0,
          },
        ]
      : []),
    ...(showFlashSales
      ? [
          {
            control: ControlType.CHECKBOX,
            name: URLParameters.FLASH_SALES,
            label: {
              text: (
                <DiscountToggleLabel
                  label={
                    lastMinuteDeals
                      ? t('vacation.oss.last_minute_deals.label')
                      : t('vacation.oss.filters.flash_sales.title')
                  }
                  icon={lastMinuteDeals ? 'last_minute_deals_rebrand' : FLASH_SALES_ICON}
                  imageBasePath={imageBasePath}
                  countOnTheLeft={!isMobile && !isSidebar}
                  count={dynamicFiltersResponse?.promotions?.flash_sales}
                  isDesktop={!isMobile}
                  tooltipText={t('vacation.oss.filters.flash_sales.tooltip')}
                  disabled={isWaiting || dynamicFiltersResponse?.promotions?.flash_sales === 0}
                  isFirst={!showPinkPeople && !showLoyaltyDeals}
                />
              ),
            },
            value: searchParams[URLParameters.FLASH_SALES] === '1',
            type: FieldType.TOGGLE,
            disabled: isWaiting || dynamicFiltersResponse?.promotions?.flash_sales === 0,
          },
        ]
      : []),
    ...(showTSH
      ? [
          {
            control: ControlType.CHECKBOX,
            name: URLParameters.TOP_SECRET_HOTEL,
            label: {
              text: (
                <DiscountToggleLabel
                  label={t('vacation.oss.filters.top_secret_hotel.title')}
                  icon="tsh"
                  imageBasePath={imageBasePath}
                  countOnTheLeft={!isMobile && !isSidebar}
                  count={dynamicFiltersResponse?.promotions?.tsh}
                  isDesktop={!isMobile}
                  tooltipText={t('vacation.oss.filters.top_secret_hotel.tooltip')}
                  disabled={isWaiting || dynamicFiltersResponse?.promotions?.tsh === 0}
                  isFirst={!showPinkPeople && !showLoyaltyDeals && !showFlashSales}
                />
              ),
            },
            value: searchParams[URLParameters.TOP_SECRET_HOTEL] === '1',
            type: FieldType.TOGGLE,
            disabled: isWaiting || dynamicFiltersResponse?.promotions?.tsh === 0,
          },
        ]
      : []),
    ...(showMyPrice
      ? [
          {
            control: ControlType.CHECKBOX,
            name: URLParameters.MY_PRICE,
            label: {
              text: (
                <DiscountToggleLabel
                  label={myPriceLabel.charAt(0).toUpperCase() + myPriceLabel.slice(1)}
                  icon="my_price"
                  imageBasePath={imageBasePath}
                  countOnTheLeft={!isMobile && !isSidebar}
                  count={dynamicFiltersResponse?.promotions?.myprice}
                  isDesktop={!isMobile}
                  tooltipText={t('vacation.oss.filters.myprice.tooltip')}
                  disabled={isWaiting || dynamicFiltersResponse?.promotions?.myprice === 0}
                  isFirst={!showPinkPeople && !showLoyaltyDeals && !showFlashSales && !showTSH}
                />
              ),
            },
            value: searchParams[URLParameters.MY_PRICE] === '1',
            type: FieldType.TOGGLE,
            disabled: isWaiting || dynamicFiltersResponse?.promotions?.myprice === 0,
          },
        ]
      : []),
  ];
  return {
    control: ControlType.CONTAINER,
    name: FilterContainer.DiscountsContainer,
    top: { text: t('vacation.oss.filters.discounts') },
    controls,
  };
};

export default getDiscountFields;
